import React, {useMemo, useReducer} from 'react';

import {apolloClientNoCache} from '../../../apolloClient';
import {
  BucketedRunsDeltaQueryDocument,
  BucketedRunsDeltaQueryQuery,
  BucketedRunsDeltaQueryQueryVariables,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {initialState, outliersReducer} from './outliersReducer';

export const useOutliersState = (config: {
  metrics?: string[];
  xAxis: string;
}) => {
  const [state, dispatch] = useReducer(outliersReducer, initialState);
  const metricsStr = JSON.stringify(config.metrics);
  React.useEffect(() => {
    dispatch({
      type: 'context',
      payload: {
        metrics: metricsStr,
      },
    });
  }, [metricsStr]);

  React.useEffect(() => {
    dispatch({
      type: 'reload',
      payload: {
        xAxis: config.xAxis,
      },
    });
  }, [config.xAxis]);

  const lastQueryTime = React.useRef(0);
  const executeQuery = React.useRef(
    (
      variables: BucketedRunsDeltaQueryQueryVariables,
      reload: boolean = false
    ) => {
      const timestamp = Date.now();
      lastQueryTime.current = timestamp;
      if (reload) {
        dispatch({
          type: 'reload',
          payload: {
            xAxis: config.xAxis,
          },
        });

        /**
         * When reloading (such as on a spec or order change) we need to clear the
         * current runs and the lastUpdated so that we don't miss data accidentally
         */
        variables.currentRuns = [];
        variables.lastUpdated = new Date(0).toISOString();
      }

      return apolloClientNoCache
        .query<
          BucketedRunsDeltaQueryQuery,
          BucketedRunsDeltaQueryQueryVariables
        >({
          context: propagateErrorsContext(),
          query: BucketedRunsDeltaQueryDocument,
          fetchPolicy: 'no-cache',
          variables,
        })
        .then(result => {
          /*
            If there is a newer query in flight, don't update the UI based on
            this result. The `query` method of the apollo client does not support
            cancellation and has no way to know if a newer query has been issued,
            so we need to check this manually. The query here is being issued
            with `query` rather than react hooks because we need to eject from 
            the react lifecycle here to avoid an infinite rendering loop.

            There are more robust ways to handle this, including:
            - eject from apollo for this query, perform a fetch and use an
              abort controller to cancel the fetch if a newer query is issued
            - use `watchQuery` to issue the query and call `unsubscribe` on the
              subscription when a newer query is issued https://github.com/apollographql/apollo-client/issues/4150#issuecomment-487412557
          */
          if (lastQueryTime.current > timestamp) {
            return;
          }
          const {runs, totalRuns} = result?.data?.project ?? {};
          const {delta, order} = runs?.deltas ?? {};

          dispatch({
            type: 'update',
            payload: {
              delta,
              order,
              totalRuns: totalRuns ?? 0,
            },
          });
        })
        .catch(error => {
          dispatch({type: 'error', error});
          console.error('error', error);
        });
    }
  );

  return useMemo(
    () => ({
      error: state.error,
      executeQuery: executeQuery.current,
      lastUpdated: state.lastUpdated,
      loading: state.loading,
      orderedRunIds: state.orderedRunIds,
      runsById: state.runsById,
      totalRuns: state.totalRuns,
    }),
    [executeQuery, state]
  );
};
